// apps/web/src/components/cockpit/ArgusMark.tsx
// Argus Sports brand tile — the "A-Eye" mark (a geometric "A" whose counter is
// an all-seeing eye) on a dark squircle. Self-contained app-icon tile for
// brand/logo placements (sidebar, login, copilot header). For an inline,
// transparent accent glyph, use ArgusIcon instead.
//
// IDs are generated per-instance via useId so multiple marks on one page never
// collide, and the background uses rounded rects (no clipPath) so it renders
// reliably at any size.

import { useId } from 'react';

export interface ArgusMarkProps {
  /** Rendered width & height in px (square). */
  size?: number;
  className?: string;
  /**
   * Solid colour for the "A" stroke and the eye. Omit for the Argus violet
   * gradient, which is what an install with no club colour gets.
   *
   * The tile behind the glyph is ALWAYS a dark squircle regardless of the app
   * theme, so pass a colour that reads on dark — `var(--club-ink-dark)`, not
   * `var(--club-ink)` (which follows the theme) and not the raw club hex
   * (which a navy-branded club would render invisible).
   */
  color?: string;
}

export function ArgusMark({ size = 32, className, color }: ArgusMarkProps) {
  const gid = useId();
  const paint = color ?? `url(#${gid})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 184 184"
      role="img"
      aria-label="Argus Sports"
      className={className}
      style={{ flexShrink: 0, display: 'block' }}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4F8DF5" />
          <stop offset="1" stopColor="#A06BFF" />
        </linearGradient>
      </defs>
      <rect width="184" height="184" rx="41" fill="#0A0C14" />
      <rect width="184" height="184" rx="41" fill={paint} opacity="0.1" />
      {/* geometric "A" whose counter is an all-seeing eye */}
      <path
        d="M56 142 L92 44 L128 142"
        fill="none"
        stroke={paint}
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* the eye — iris ring + pupil */}
      <circle cx="92" cy="112" r="15" fill="none" stroke={paint} strokeWidth="9" />
      <circle cx="92" cy="112" r="5" fill={paint} />
      <rect x="1" y="1" width="182" height="182" rx="40" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
    </svg>
  );
}
